// BookTickets/hooks/useBookingCartWatcher.ts
'use client'

import { useCallback, useEffect, useRef } from 'react'
import type { BookingAction, BookingState } from '@booking/state/booking.reducer'
import type { Cart, VoucherCart } from '@booking/types/booking'
import { apiGetCart } from '@booking/services/bookingApi'
import {
  setBookingTags /*, breadcrumb, captureBookingMessage*/,
} from '@booking/services/sentryBooking'
import { usePathname, useSearchParams } from 'next/navigation'
import trackingGa4, {
  transformLinesToItems,
  transformVoucherLinesToItems,
} from '../services/trackingGa4'
import {
  createTransaction,
  finalizeTransaction,
  getLocalCart,
  getSession,
  setPaypalTransaction,
} from '@booking/services/api'
import { stageNumberOf } from '@booking/lib/bookingConfig'
import type { BookingConfig } from '@booking/types/bookingConfig'
import { BookingActionsShape } from '../state/booking.context'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'

const STORAGE_CART_ID = 'book.cartId'

type Args = {
  state: BookingState
  actions: BookingActionsShape
  dispatch: React.Dispatch<BookingAction>
  trackHistory: (stage: number) => void
  locale: string
  bookingConfig: BookingConfig
}

async function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function useBookingCartWatcher({
  state,
  actions,
  dispatch,
  trackHistory,
  bookingConfig,
}: Args) {
  const params = useSearchParams()
  const router = useRouter()
  const path = usePathname()
  const t = useTranslations('booking')
  const ran = useRef(false)
  const freeRef = useRef(false)

  const { rebuildCart } = actions

  const urlStage = params.get('stage')
  const abandonCartId = params.get('abid')
  const cartId = params.get('cartId')
  const paymentMethod = params.get('paymentMethod')
  const associate = params.get('associate')
  const free = params.get('free')

  const handleSoldTransaction = useCallback(
    async (cart: Cart | VoucherCart) => {
      dispatch({ type: 'SET_STAGE', stage: stageNumberOf(bookingConfig, 'thankyou') })
      dispatch({ type: 'SET_LOADING', loading: null })

      if (!cart) {
        console.error('Handle sold transaction: cart not defined')
      }

      finalizeTransaction(cart.id)

      if (cart.type === 'standard') {
        trackingGa4.purchase(cart, transformLinesToItems({ cart }))
      } else if (cart.type === 'voucher') {
        trackingGa4.voucherPurchase(cart, transformVoucherLinesToItems({ cart }))
      }

      // add success param after redirecting back from payment processor, to allow tracking in GA
      // as payment processor always redirects to same URL, no matter if success or fail
      // Build the new query params
      const searchParams = new URLSearchParams(params.toString())
      searchParams.set('success', 'true')
      router.replace(`${path}?${searchParams.toString()}`)
    },
    [bookingConfig, dispatch, router, params, path],
  )

  const handleFailedTransaction = useCallback(
    async (cart: Cart | VoucherCart) => {
      dispatch({
        type: 'SET_LOADING',
        loading: { message: 'Transaction failed .. rebuilding cart' },
      })
      const rebuiltCart = await rebuildCart(cart)
      if (rebuiltCart) dispatch({ type: 'SET_CART', cart: rebuiltCart })
      dispatch({ type: 'SET_STAGE', stage: stageNumberOf(bookingConfig, 'payment') })
      dispatch({ type: 'SET_LOADING', loading: null })

      dispatch({
        type: 'SET_ERROR',
        error: {
          level: 'medium',
          scope: 'local',
          message: t('ticket.payment.error'),
        },
      })
    },
    [bookingConfig, rebuildCart, dispatch, t],
  )

  const handlePaypalPrerequisite = useCallback(
    async (cart: Cart | VoucherCart) => {
      //const query = new URLSearchParams(location.search)
      const payerId = params.get('PayerID')
      const paymentId = params.get('paymentId')
      const token = params.get('token')

      if (!payerId || !paymentId || !token) {
        handleFailedTransaction(cart)
        return false
      }

      await setPaypalTransaction(cart.id, {
        payerId: payerId,
        paymentId: paymentId,
        token: token,
      })
    },
    [handleFailedTransaction, params],
  )

  const handleReservedCart = useCallback(
    async (cart: Cart | VoucherCart, paymentMethod: string) => {
      //paypal needs to be set before calling create cart
      if (paymentMethod === 'paypal') {
        await handlePaypalPrerequisite(cart)
      }

      // transaction only needs creating if its card/paypal (i.e. not mbway)
      const MAX_ATTEMPTS = 5
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          console.log(`Attempt ${attempt}...`)
          const res = await createTransaction(cart.id)
          if (!res) break // this should never come back as null

          if (res.status === 'RESERVED') continue
          if (res.status === 'OPEN' || res.status === 'CANCELLED') {
            handleFailedTransaction(cart)
            break
          }

          if (res.status === 'SOLD') {
            console.log('Handling sold transaction...')
            handleSoldTransaction(cart)
            break
          }
        } catch (error) {
          console.error(`Attempt ${attempt} failed:`, error)
        }

        // Wait before next attempt (unless it's the last one)
        if (attempt < MAX_ATTEMPTS) {
          await sleep(1800)
        }

        if (attempt === MAX_ATTEMPTS) {
          handleFailedTransaction(cart)
        }
      }

      if (cart.type === 'standard') {
        trackingGa4.addPaymentInfo(paymentMethod, transformLinesToItems({ cart }))
      }
    },
    [handleFailedTransaction, handleSoldTransaction, handlePaypalPrerequisite],
  )

  const loadCart = useCallback(
    async (cartId: string, paymentMethod: string) => {
      dispatch({ type: 'SET_LOADING', loading: { message: 'Checking transaction status...' } })
      const cart = await apiGetCart(cartId)
      if (!cart) return
      dispatch({ type: 'SET_CART', cart: cart })

      if (
        (cart.status === 'RESERVED' || (cart.total === 0 && cart.status === 'OPEN')) &&
        paymentMethod !== 'mbway'
      ) {
        // this only needs to fire for cc and paypal, not for mbway (handled on previous step)
        await handleReservedCart(cart, paymentMethod)
      } else if (cart && cart.status === 'SOLD') {
        const st = associate
          ? stageNumberOf(bookingConfig, 'associate')
          : stageNumberOf(bookingConfig, 'thankyou')
        dispatch({ type: 'SET_STAGE', stage: st })
        dispatch({ type: 'SET_LOADING', loading: null })
        finalizeTransaction(cart.id)
      } else {
        // when status is OPEN or CANCELLED?
        dispatch({ type: 'SET_LOADING', loading: null })
        trackHistory(1)
      }
    },
    [bookingConfig, dispatch, trackHistory, handleReservedCart, associate],
  )

  const fetchAbandonCart = useCallback(
    async (abandonCartId: string) => {
      const cart = await getLocalCart(abandonCartId)
      if (!cart) return
      const session = await getSession(cart.sessionId)

      dispatch({
        type: 'SET_TICKET_QUANTITIES',
        quantities: cart.bookingItems,
      })

      dispatch({
        type: 'SET_SESSION',
        session: session,
      })

      trackHistory(2)
    },
    [dispatch, trackHistory],
  )

  const initialStateCheckForStage = useCallback(
    (stage: number) => {
      console.log('Running state check...')
      // cart must always exist after stage 2
      if (stage > 2 && stage < 5 && state.cart === null) {
        trackHistory(1)
      }

      // quantities should always be filled out after quantities stage
      if (stage > 1 && stage < 5 && state.quantities.length === 0) {
        trackHistory(1)
      }
    },
    [state.cart, state.quantities, trackHistory],
  )

  // this fires when transactions are free (i.e. voucher or 100 off discount)
  useEffect(() => {
    if (freeRef.current) return
    if (state.cart && free === 'true') {
      freeRef.current = true
      console.log('Cart required no payment...')
      handleReservedCart(state.cart, 'free')
    }
  }, [free, state.cart, handleReservedCart])

  // initial hook which only fires once on the initial page load (i.e. redirect from payment processor)
  useEffect(() => {
    if (ran.current) return
    ran.current = true

    //const abandonCartId = params.get('abid')
    console.log('Running initial hook..')
    console.log(cartId)
    console.log(abandonCartId)

    if (cartId) {
      loadCart(cartId, paymentMethod || 'card')
    } else if (abandonCartId) {
      fetchAbandonCart(abandonCartId)
    } else if (urlStage) {
      const firstStageId = bookingConfig.stages[0]

      if (urlStage !== firstStageId) {
        // User landed mid-flow without going through checkout — reset to start
        const qs = new URLSearchParams(params.toString())
        qs.set('stage', firstStageId)
        //qs.set('stage', firstStageId)
        router.replace(`?${qs.toString()}`)
      }
    }
  }, [
    loadCart,
    cartId,
    paymentMethod,
    abandonCartId,
    fetchAbandonCart,
    initialStateCheckForStage,
    bookingConfig,
    urlStage,
    trackHistory,
    router,
    params,
  ])

  // keep storage in sync if cart changes later
  useEffect(() => {
    if (!state.cart?.id) return
    sessionStorage.setItem(STORAGE_CART_ID, state.cart.id)
    setBookingTags({ cartId: state.cart.id })
  }, [state.cart?.id])
}
